import React, { useMemo } from "react";
import { AvatarRing } from "../../../../common/comms/AvatarRing";
import { EmptyState } from "../../../../common/comms/EmptyState";
import { useCommsIdentity } from "../../../../hooks/comms/useCommsIdentity";
import { contactCadence } from "./coachOverviewDerive";

/** Bar fill per tone. Full class strings so Tailwind keeps them. */
const BAR_TONE = {
  success: "bg-success",
  warn: "bg-internal",
  danger: "bg-danger",
};

/** Row background and figure colour for the "going quiet" list. */
const QUIET_TONE = {
  danger: { row: "bg-danger-bg", text: "text-danger-dark" },
  warn: { row: "bg-internal-bg", text: "text-internal-text" },
};

/**
 * How long a client has been silent, as a short label.
 * @param {number|null} days
 */
const silenceLabel = (days) => (days == null ? "never" : `${days}d`);

/**
 * "How recently each room was active" — the cadence panel.
 *
 * IMPORTANT ON MEANING: this reads `last_activity_at`, which is the last
 * message either way. The design's title ("how recently each client heard from
 * her") would need a `last_coach_message_at` the endpoint does not return, so
 * the panel is titled for what the data actually says. A client who messages
 * daily but is never answered would sit in the "today" bucket here — which is
 * exactly why the honest title matters.
 *
 * @param {Object} props
 * @param {Array<Object>} props.clients - `/oversight/coaches/{id}/clients` rows.
 * @param {boolean} [props.loading]
 */
export function ContactCadence({ clients, loading = false }) {
  const { getClientName } = useCommsIdentity();
  const { buckets, total, quiet, quietTotal } = useMemo(
    () => contactCadence(clients),
    [clients]
  );

  if (loading) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="h-4 w-56 rounded bg-gray-100 animate-pulse" />
      </div>
    );
  }

  if (total === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <EmptyState title="No clients assigned" />
      </div>
    );
  }

  const widest = Math.max(...buckets.map((b) => b.count), 1);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <div className="flex flex-wrap items-baseline gap-2.5 mb-4">
        <h2 className="text-base font-bold text-gray-900">How recently each room was active</h2>
        <span className="sm:ml-auto text-[11px] text-gray-400">
          {total} assigned client{total === 1 ? "" : "s"}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
        <div className="flex flex-col gap-3">
          {buckets.map((b) => (
            <div key={b.id} className="flex items-center gap-3">
              <span className="w-32 sm:w-36 text-xs text-gray-700 flex-shrink-0">{b.label}</span>
              <div className="flex-1 h-2.5 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className={`h-full rounded-full ${BAR_TONE[b.tone]}`}
                  style={{ width: `${(b.count / widest) * 100}%` }}
                />
              </div>
              <span className="w-7 text-[13px] font-bold text-gray-800 text-right">{b.count}</span>
            </div>
          ))}
        </div>

        <div>
          <p className="mb-2 text-[11px] uppercase tracking-wide text-gray-500">
            {quietTotal === 0
              ? "Nothing has gone quiet"
              : `Going quiet · longest ${quiet.length} of ${quietTotal}`}
          </p>
          <div className="flex flex-col gap-2">
            {quiet.map(({ client, days }) => {
              const name =
                getClientName(client.client_id) ||
                (client.display_id != null ? `#${client.display_id}` : "Client");
              const tone = days == null || days >= 15 ? QUIET_TONE.danger : QUIET_TONE.warn;
              const colorIndex =
                client.client_id != null ? String(client.client_id).charCodeAt(0) % 8 : 0;
              return (
                <div
                  key={client.client_id}
                  className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg ${tone.row}`}
                >
                  <AvatarRing name={name} colorIndex={colorIndex} size="sm" />
                  <span className="text-[13px] font-semibold text-gray-800 flex-1 truncate">
                    {name}
                  </span>
                  <span className={`text-xs font-semibold ${tone.text}`}>
                    {silenceLabel(days)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <p className="mt-4 text-[11px] text-gray-400">
        Based on the last message either way. "Last heard from the coach" needs a field the
        roster endpoint does not return yet.
      </p>
    </div>
  );
}

export default ContactCadence;
